import React from 'react';
import { Link } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';

function Avatar({ name }) {
  const initials = name?.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() || '?';
  return (
    <div className="shrink-0 w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
      {initials}
    </div>
  );
}

function MemberRow({ member, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-3 first:pt-0 last:pb-0">
      <div className="flex items-center gap-3 min-w-0">
        <Avatar name={member.display_name} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-gray-900">{member.display_name}</p>
            {member.has_active_license && (
              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                {member.active_license_name}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">{member.email}</p>
        </div>
      </div>
      {action}
    </div>
  );
}

export default function ProgressIndex({ facility, members = [] }) {
  const withTraining = members.filter((m) => m.has_active_license);
  const withoutTraining = members.filter((m) => !m.has_active_license);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Progress Tracking</h1>
          <p className="mt-1 text-sm text-gray-500">{facility.name}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Active Training</h2>
            <span className="ml-auto text-sm text-gray-400">{withTraining.length} {withTraining.length === 1 ? 'member' : 'members'}</span>
          </div>

          {withTraining.length === 0 ? (
            <div className="text-center py-10">
              <div className="mx-auto w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
              </div>
              <p className="text-gray-600">No team members have training assigned yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {withTraining.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  action={
                    <Link
                      href={m.progress_url}
                      className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition"
                    >
                      View Progress
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                    </Link>
                  }
                />
              ))}
            </div>
          )}
        </div>

        {withoutTraining.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-amber-50 text-amber-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">No Training Assigned</h2>
              <span className="ml-auto text-sm text-gray-400">{withoutTraining.length}</span>
            </div>
            <div className="divide-y divide-gray-100">
              {withoutTraining.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  action={
                    <Link
                      href="/assign-training"
                      className="shrink-0 inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-lg text-indigo-600 border border-indigo-200 bg-white hover:bg-indigo-50 transition"
                    >
                      Assign Training
                    </Link>
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
